import { Box, ListItemText, Stack } from '@mui/material'
import { C1AutocompleteSingleAsyncHook } from 'components/core/autocomplete'
import { AVATAR_SIZES } from 'components/core/avatar/AvatarSizes'
import { APIError } from 'components/core/errors/api'
import { RenderedSelectOneProps, renderLabel, SelectOne, SelectOneHookProps } from 'components/core/search/index'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { C1EntitlementDisplayName, getEntitlementDisplayNameTextOnly } from 'components/pages/entitlement/display-name'
import { ResourceDisplayName } from 'components/pages/entitlement/resource'
import { AppValue } from 'lib/actions/apps'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppEntitlementProxyValue } from 'lib/actions/apps/app-proxy-entitlement'
import { AppResourceValue } from 'lib/actions/apps/app-resources'
import { App, AppRef, SearchAppsExpandedRequest } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import {
    AppEntitlementProxy,
    ProxyBindingDirection,
    SearchAppEntitlementProxyBindingsRequest,
} from 'pbts/c1/api/app/v1/app_entitlement_proxy'
import {
    AppManagedStateBindingRef,
    AppManagedStateBindingView,
    SearchAppManagedStateBindingsRequest,
} from 'pbts/c1/api/app/v1/app_managed'
import { AppResource, AppResourceRef, AppResourceType, SearchAppResourcesRequest } from 'pbts/c1/api/app/v1/app_resource'
import { DelegatedProvision } from 'pbts/c1/api/policy/v1/policy'
import React, { ComponentProps, FC } from 'react'
import { useIntl } from 'react-intl'
import { appResourceSearchApi } from 'redux_toolkit/app-resources'
import { AppManagedStateBindingValue, appSearchApi, unmanagedAppsSearchApi } from 'redux_toolkit/apps'
import { appEntitlementProxyBindingsSearchApi, getAppEntitlementApi } from 'redux_toolkit/entitlements'

export const getAppDescription = (app: AppValue): string => {
    return app?.description || ''
}

export const AppRenderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AppValue): React.ReactElement => {
    const appDescription = getAppDescription(option)

    return <li {...props} key={option.id}>
        <Stack width="100%">
            <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                <AppDisplayName app={option} readOnly maxWidth="100%" />
            </Box>
            {appDescription && <ListItemText
                sx={{ ml: `${AVATAR_SIZES.md.width}px`, pl: 1 }}
                primary={appDescription}
                primaryTypographyProps={{
                    variant: 'caption',
                    title: appDescription,
                    noWrap: true,
                }}
            />}
        </Stack>
    </li>
}

const appResourceRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppResourceValue,
): React.ReactElement => (
    <li {...props} key={`${option.appId}-${option.id}`}>
        <ResourceDisplayName
            resource={option}
            resourceType={option.appResourceType}
            appName={option.app?.displayName}
            includeDetails
            includeType
            readOnly
        />
    </li>
)

const unmanagedAppRenderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: AppManagedStateBindingValue): React.ReactElement => (
    <li {...props} key={option.id}>
        <AppDisplayName app={App.create({ displayName: option.displayName })} readOnly fullWidth />
    </li>
)

export type SelectOneAppSimpleComponentProps = Omit<RenderedSelectOneProps<AppValue>, 'placeholder'> & {
    placeholder: string
}

/**
 * @deprecated Use SelectOneAppHook instead
 */
export const SelectOneAppSimpleComponent = (props: SelectOneAppSimpleComponentProps): React.ReactElement => (
    <SelectOne<AppValue> {...props} renderOption={AppRenderOption} renderLabel={renderLabel} fullWidth />
)

export type SelectOneAppResourceTypeSimpleComponentProps = RenderedSelectOneProps<AppResourceType>

export const SelectOneAppResourceTypeSimpleComponent = (
    props: SelectOneAppResourceTypeSimpleComponentProps,
): React.ReactElement => <SelectOne<AppResourceType> {...props} renderLabel={renderLabel} />

export const getEntitlementDescription = (entitlement: AppEntitlementValue): string => {
    const { appResource } = entitlement
    return entitlement?.description || appResource?.customDescription || appResource?.description || ''
}

export const appEntitlementRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppEntitlement,
): React.ReactElement => {
    const entitlementDescription = getEntitlementDescription(option)

    return <li {...props} key={`${option.id}-${option.appId}`}>
        <Stack width="100%">
            <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                <C1EntitlementDisplayName
                    entitlement={option}
                    readOnly
                />
            </Box>
            <ListItemText
                sx={{ ml: `${AVATAR_SIZES.md.width}px`, pl: 1 }}
                primary={entitlementDescription}
                primaryTypographyProps={{
                    variant: 'caption',
                    title: entitlementDescription,
                    noWrap: true,
                }}
            />
        </Stack>
    </li>
}

export type SelectOneAppEntitlementSimpleComponentProps = RenderedSelectOneProps<AppEntitlementValue>

export const SelectOneAppEntitlementSimpleComponent = (
    props: SelectOneAppEntitlementSimpleComponentProps,
): React.ReactElement => (
    <SelectOne<AppEntitlementValue> {...props} renderOption={appEntitlementRenderOption} renderLabel={getEntitlementDisplayNameTextOnly} />
)

const appEntitlementProxyValueRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AppEntitlementProxyValue,
): React.ReactElement => <li {...props} key={option.id}>
    <C1EntitlementDisplayName
        entitlement={{
            ...option.srcAppEntitlement,
            app: option.srcApp,
        }}
        readOnly
    />
</li>

export type SearchIncomingProxyAppEntitlementsProps = Omit<
    ComponentProps<
        typeof C1AutocompleteSingleAsyncHook<SearchAppEntitlementProxyBindingsRequest, AppEntitlementProxyValue>
    >,
    'Req' | 'hook' | 'renderOption' | 'renderLabel' | 'initialValue' | 'type'
> & {
    destAppEntitlement: AppEntitlement
    delegatedStep?: DelegatedProvision
    app: App
}

const appEntitlementProxyValueRenderLabel = (option: AppEntitlementProxyValue): string =>
    (option ? `${option?.srcApp.displayName}: ${option?.srcAppEntitlement.displayName}` : '')

export const SearchIncomingProxyAppEntitlements: React.FC<SearchIncomingProxyAppEntitlementsProps> = ({
    destAppEntitlement,
    app,
    delegatedStep,
    error,
    helperText,
    onSelected,
    ...rest
}) => {
    const actionable = !!delegatedStep

    const { data, error: apiError, isError } = getAppEntitlementApi.useGet(
        {
            appId: delegatedStep ? delegatedStep.appId : '',
            id: delegatedStep ? delegatedStep.entitlementId : '',
            expandMask: { paths: ['app_id'] },
        },
        { actionable },
    )

    if (isError) {
        return <APIError err={apiError} />
    }
    const appEntitlementProxy = !!data && actionable ? AppEntitlementProxy.create({
        srcAppId: delegatedStep?.appId,
        srcAppEntitlementId: delegatedStep?.entitlementId,
        dstAppId: destAppEntitlement.appId,
        dstAppEntitlementId: destAppEntitlement.id,
    }) : undefined

    const proxyBinding = appEntitlementProxy ? {
        ...appEntitlementProxy,
        id: `${appEntitlementProxy.srcAppEntitlementId}:${appEntitlementProxy.dstAppEntitlementId}`,
        displayName: `${data?.displayName} → ${appEntitlementProxy.dstAppEntitlementId}`,
        srcApp: data?.app || App.create({}),
        dstApp: app,
        srcAppEntitlement: AppEntitlement.create({
            id: data?.id,
            displayName: data?.displayName,
            appId: data?.appId,
        }),
        dstAppEntitlement: destAppEntitlement,
    } : undefined

    const req = {
        create: (q: Partial<SearchAppEntitlementProxyBindingsRequest>) => SearchAppEntitlementProxyBindingsRequest.create({
            query: q.query,
            directions: [ProxyBindingDirection.INBOUND],
            appId: destAppEntitlement.appId,
            appEntitlementId: destAppEntitlement.id,
            expandMask: { paths: ['*'] },
            implicit: true,
        }),
    }

    return !proxyBinding && actionable ? null : (
        <C1AutocompleteSingleAsyncHook<SearchAppEntitlementProxyBindingsRequest, AppEntitlementProxyValue>
            {...rest}
            hook={appEntitlementProxyBindingsSearchApi.useLazySearchAppEntitlementProxyBindings}
            Req={req}
            renderOption={appEntitlementProxyValueRenderOption}
            renderLabel={appEntitlementProxyValueRenderLabel}
            initialValue={proxyBinding}
            type={AppEntitlementProxy}
            onSelected={onSelected}
            helperText={helperText}
            error={error}
        />
    )
}

type SelectOneAppHookProps = SelectOneHookProps<SearchAppsExpandedRequest, AppValue> & {
    app?: App
    initAppId?: string
    onSelected: (users: App | null) => void
    hideLabel?: boolean
    onlyV2Connectors?: boolean
    excludeAppIds?: string[]
}
export const SelectOneAppHook: FC<SelectOneAppHookProps> = ({
    app, hideLabel = false, initAppId, onlyV2Connectors = false, excludeAppIds, ...rest }) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<SearchAppsExpandedRequest>) =>
            SearchAppsExpandedRequest.create({
                ...q,
                onlyV2Connectors,
                excludeIds: excludeAppIds,
            }),
    }

    return <C1AutocompleteSingleAsyncHook<SearchAppsExpandedRequest, AppValue>
        placeholder={intl.formatMessage({ id: 'SelectOneAppHook_placeholder', defaultMessage: ' Select an app' })}
        label={hideLabel
            ? undefined
            : intl.formatMessage({ id: 'SelectOneAppHook_label', defaultMessage: 'App' })}
        {...rest}
        initialRef={initAppId ? AppRef.create({ id: initAppId }) : undefined}
        type={App}
        hook={appSearchApi.useLazySearchAppsExpanded}
        Req={req}
        renderOption={AppRenderOption}
        renderLabel={renderLabel}
        initialValue={app}
    />
}

export type SelectOneAppResourceHookProps = SelectOneHookProps<SearchAppResourcesRequest, AppResourceValue> & {
    baseRequest?: Partial<SearchAppResourcesRequest>
    appResource?: AppResourceValue
    initAppResourceRef?: {
        id: string
        appId: string
        appResourceTypeId: string
    }
    onSelected: (ar: AppResourceValue) => void
    hideLabel?: boolean
}
export const SelectOneAppResourceHook: FC<SelectOneAppResourceHookProps> = ({
    appResource,
    initAppResourceRef,
    baseRequest,
    hideLabel = false,
    ...rest
}) => {
    const intl = useIntl()

    const req = {
        create: (q: Partial<SearchAppResourcesRequest>) => SearchAppResourcesRequest.create({ ...q, ...baseRequest }),
    }

    return <C1AutocompleteSingleAsyncHook<SearchAppResourcesRequest, AppResourceValue>
        placeholder={intl.formatMessage({ id: 'SelectOneAppResourceHook_placeholder', defaultMessage: ' Select a resource' })}
        label={hideLabel ? undefined : intl.formatMessage({ id: 'SelectOneAppResourceHook_label', defaultMessage: 'Resource' })}
        {...rest}
        type={AppResource}
        hook={appResourceSearchApi.useLazySearchAppResources}
        reqExpandMask={{ paths: ['*'] }}
        Req={req}
        renderOption={appResourceRenderOption}
        renderLabel={renderLabel}
        initialValue={appResource}
        initialRef={initAppResourceRef ? AppResourceRef.create(initAppResourceRef) : undefined}
    />
}

type SelectOneUnmanagedAppHookProps = SelectOneHookProps<SearchAppManagedStateBindingsRequest, AppManagedStateBindingValue> & {
    unmanagedApp?: AppManagedStateBindingValue
    initUnmanagedRef?: AppManagedStateBindingRef
    onSelected: (unmanagedApp: AppManagedStateBindingValue | null) => void
    hideLabel?: boolean
}
export const SelectOneUnmanagedAppHook: FC<SelectOneUnmanagedAppHookProps> = (
    { unmanagedApp, hideLabel = false, initUnmanagedRef, ...rest },
) => {
    const intl = useIntl()

    return <C1AutocompleteSingleAsyncHook<SearchAppManagedStateBindingsRequest, AppManagedStateBindingValue>
        placeholder={intl.formatMessage({ id: 'SelectOneUnmanagedAppHook_placeholder', defaultMessage: ' Select an unmanaged app' })}
        label={hideLabel
            ? undefined
            : intl.formatMessage({ id: 'SelectOneUnmanagedAppHook_label', defaultMessage: 'App' })}
        {...rest}
        initialRef={initUnmanagedRef ? AppManagedStateBindingRef.create(initUnmanagedRef) : undefined}
        type={AppManagedStateBindingView}
        hook={unmanagedAppsSearchApi.useLazySearch}
        Req={SearchAppManagedStateBindingsRequest}
        renderOption={unmanagedAppRenderOption}
        renderLabel={renderLabel}
        initialValue={unmanagedApp}
    />
}
